import { onBeforeUnmount } from "vue";

const CHANNEL_NAME = "gaia-sip";

export interface SipFocusRequestMessage {
  type: "focus-request";
  timestamp: number;
}

export interface SipHeartbeatMessage {
  type: "heartbeat";
  timestamp: number;
  isPopup: boolean;
  windowId: string;
}

export interface SipCallStateSnapshot {
  isRegistered: boolean;
  // Interno SIP del leader (es. "205"). Propagato ai follower così la sezione
  // centralino, che gira nella main app (follower), sa quale card/chip è la
  // propria e può riconciliarne lo stato con quello reale della barra quando la
  // registrazione è caduta (GAIA-2589 Scen. 2).
  extension: string;
  isInCall: boolean;
  isCallEstablished: boolean;
  callStatus: string | null;
  callDuration: number;
  phoneNumber: string;
  callDirection: "incoming" | "outgoing" | null;
  hasIncomingRing: boolean;
  connectionFailureMessage: string | null;
  // Motivo di pausa dell'operatore nella coda Asterisk (es. "Back Office",
  // "Pausa Utente"); null se non è in pausa. Propagato dal leader per alimentare
  // gli stati "Pausa"/"Back Office" del SipStatusChip nell'header. È già
  // risolto: null quando l'interno non è raggiungibile (GAIA-2694).
  pauseReason: string | null;
  // Asterisk non raggiunge l'interno del leader (device Invalid/Unavailable fra
  // i membri di coda) mentre la barra si crede registrata. Propagato perché il
  // chip in header deve dire la stessa cosa della card in /switchboard, invece
  // di annunciare "Back Office" su un interno che non riceve chiamate (GAIA-2694).
  queueMemberUnreachable: boolean;
}

export interface SipCallStateSnapshotMessage {
  type: "call-state-snapshot";
  timestamp: number;
  payload: SipCallStateSnapshot;
}

export interface SipSnapshotRequestMessage {
  type: "request-snapshot";
  timestamp: number;
}

export interface SipPopupClosingMessage {
  type: "popup-closing";
  timestamp: number;
}

export interface SipPopupShutdownMessage {
  type: "popup-shutdown";
  timestamp: number;
  reason: "role-changed" | "manual";
}

export interface SipMainAliveMessage {
  type: "main-alive";
  timestamp: number;
  windowId: string;
}

export interface SipPrefillNumberMessage {
  type: "prefill-number";
  timestamp: number;
  phoneNumber: string;
}

export type SipCommandAction = "dial" | "answer" | "hangup" | "dtmf" | "setPause" | "focus";

export interface SipCommandMessage {
  type: "command";
  timestamp: number;
  action: SipCommandAction;
  payload?: string | null;
}

export interface SipIncomingCallMessage {
  type: "incoming-call";
  timestamp: number;
  phoneNumber: string;
  /**
   * Linea da cui è entrata la chiamata, quando il centralino la dichiara nel
   * display-name. Viaggia insieme al numero e non in un messaggio a parte:
   * a differenza della scheda del lead non è il risultato di una lookup, è già
   * nell'INVITE, e separarla farebbe lampeggiare il banner due volte.
   * Opzionale: le chiamate senza CID Name Prefix non la portano.
   */
  callerLine?: string | null;
}

/**
 * La chiamata in arrivo proviene da un contatto già esistente in Gaia, di cui
 * l'owner ha risolto la scheda.
 *
 * È un messaggio SEPARATO da `incoming-call` di proposito: la lookup del lead è
 * un'operazione di rete che arriva dopo lo squillo, e ri-pubblicare
 * `incoming-call` con l'URL dentro farebbe risuonare audio, notifica di sistema
 * e title-flash una seconda volta. `incoming-call` annuncia la chiamata,
 * `incoming-lead` la arricchisce (GAIA-2719).
 */
export interface SipIncomingLeadMessage {
  type: "incoming-lead";
  timestamp: number;
  /** Numero della chiamata a cui la scheda si riferisce: serve al consumatore
   *  per scartare un messaggio in ritardo relativo alla chiamata precedente. */
  phoneNumber: string;
  targetUrl: string;
}

/**
 * Il tentativo di rispondere è fallito. Va pubblicato perché il click su
 * "Rispondi" arriva dal banner nella tab in cui l'operatore lavora, non dalla
 * finestra della barra: mostrare l'errore solo lì significherebbe non mostrarlo
 * (la barra è dietro, spesso minimizzata). Prima di GAIA-2664 questo caso non
 * produceva feedback da nessuna parte e il tasto verde sembrava rotto.
 */
export interface SipAnswerFailedMessage {
  type: "answer-failed";
  timestamp: number;
  /** Numero della chiamata che non si è riusciti a rispondere. */
  phoneNumber: string;
  /** `no-call` = non c'era più nulla da rispondere; `accept-failed` = accept fallito. */
  reason: "no-call" | "accept-failed";
}

/**
 * Esito di un trasferimento cieco (GAIA-2776).
 *
 * Va in broadcast per la stessa ragione di `answer-failed`: l'operatore lavora
 * nella scheda Gaia, non nella finestra della barra, che è spesso dietro e
 * throttlata da Chromium. Mostrare "il collega non ha risposto" solo nella
 * barra significa non mostrarlo — con la differenza, qui, che il cliente è
 * tornato in linea e sta aspettando che qualcuno gli parli.
 */
export interface SipTransferOutcomeMessage {
  type: "transfer-outcome";
  timestamp: number;
  /** Interno verso cui si era tentato il trasferimento. */
  toExtension: string;
  outcome: "completed" | "noanswer" | "busy" | "failed";
}

export type SipBroadcastMessage =
  | SipFocusRequestMessage
  | SipHeartbeatMessage
  | SipCallStateSnapshotMessage
  | SipSnapshotRequestMessage
  | SipPopupClosingMessage
  | SipPopupShutdownMessage
  | SipMainAliveMessage
  | SipPrefillNumberMessage
  | SipCommandMessage
  | SipIncomingCallMessage
  | SipIncomingLeadMessage
  | SipAnswerFailedMessage
  | SipTransferOutcomeMessage;

export interface UseSipBroadcastReturn {
  postFocusRequest: () => void;
  onFocusRequest: (handler: () => void) => void;
  postHeartbeat: (isPopup: boolean, windowId: string) => void;
  onHeartbeat: (handler: (msg: SipHeartbeatMessage) => void) => void;
  postCallStateSnapshot: (payload: SipCallStateSnapshot) => void;
  onCallStateSnapshot: (handler: (msg: SipCallStateSnapshotMessage) => void) => void;
  requestSnapshot: () => void;
  onSnapshotRequest: (handler: () => void) => void;
  postPopupClosing: () => void;
  onPopupClosing: (handler: () => void) => void;
  postPopupShutdown: (reason: SipPopupShutdownMessage["reason"]) => void;
  onPopupShutdown: (handler: (msg: SipPopupShutdownMessage) => void) => void;
  postMainAlive: (windowId: string) => void;
  onMainAlive: (handler: (msg: SipMainAliveMessage) => void) => void;
  onPrefillNumber: (handler: (msg: SipPrefillNumberMessage) => void) => void;
  postCommand: (action: SipCommandAction, payload?: string | null) => void;
  onCommand: (handler: (msg: SipCommandMessage) => void) => void;
  postIncomingCall: (phoneNumber: string, callerLine?: string | null) => void;
  onIncomingCall: (handler: (msg: SipIncomingCallMessage) => void) => void;
  onIncomingLead: (handler: (msg: SipIncomingLeadMessage) => void) => void;
  postAnswerFailed: (phoneNumber: string, reason: SipAnswerFailedMessage["reason"]) => void;
  onAnswerFailed: (handler: (msg: SipAnswerFailedMessage) => void) => void;
  postTransferOutcome: (toExtension: string, outcome: SipTransferOutcomeMessage["outcome"]) => void;
  onTransferOutcome: (handler: (msg: SipTransferOutcomeMessage) => void) => void;
}

/**
 * Canale cross-tab usato da:
 *   1. Il bottone "Vai alla scheda attiva" dei follower per segnalare al leader
 *      di richiamare attenzione (lampeggio titolo/favicon).
 *   2. Il popup SIP dedicato per pubblicare heartbeat e snapshot di stato chiamata
 *      verso le tab follower (così la main app mostra lo stato corrente nel chip).
 *   3. Le tab non-popup per richiedere uno snapshot iniziale al boot e sapere
 *      se esiste un popup vivo prima di reclamare la leadership SIP.
 *
 * Serve perché i browser bloccano window.focus()/window.open() cross-tab fra
 * browsing context groups indipendenti. Il leader, ricevuto il segnale di focus,
 * usa l'API di tab title/favicon (che funziona sempre) per farsi notare nella
 * barra delle schede.
 */
export function useSipBroadcast(): UseSipBroadcastReturn {
  const channel = new BroadcastChannel(CHANNEL_NAME);

  const focusHandlers: Array<() => void> = [];
  const heartbeatHandlers: Array<(msg: SipHeartbeatMessage) => void> = [];
  const snapshotHandlers: Array<(msg: SipCallStateSnapshotMessage) => void> = [];
  const snapshotRequestHandlers: Array<() => void> = [];
  const popupClosingHandlers: Array<() => void> = [];
  const popupShutdownHandlers: Array<(msg: SipPopupShutdownMessage) => void> = [];
  const mainAliveHandlers: Array<(msg: SipMainAliveMessage) => void> = [];
  const prefillHandlers: Array<(msg: SipPrefillNumberMessage) => void> = [];
  const commandHandlers: Array<(msg: SipCommandMessage) => void> = [];
  const incomingCallHandlers: Array<(msg: SipIncomingCallMessage) => void> = [];
  const incomingLeadHandlers: Array<(msg: SipIncomingLeadMessage) => void> = [];
  const answerFailedHandlers: Array<(msg: SipAnswerFailedMessage) => void> = [];
  const transferOutcomeHandlers: Array<(msg: SipTransferOutcomeMessage) => void> = [];

  channel.addEventListener("message", (event: MessageEvent<SipBroadcastMessage>) => {
    const data = event.data;
    if (!data) return;
    switch (data.type) {
      case "focus-request":
        focusHandlers.forEach((h) => h());
        return;
      case "heartbeat":
        heartbeatHandlers.forEach((h) => h(data));
        return;
      case "call-state-snapshot":
        snapshotHandlers.forEach((h) => h(data));
        return;
      case "request-snapshot":
        snapshotRequestHandlers.forEach((h) => h());
        return;
      case "popup-closing":
        popupClosingHandlers.forEach((h) => h());
        return;
      case "popup-shutdown":
        popupShutdownHandlers.forEach((h) => h(data));
        return;
      case "main-alive":
        mainAliveHandlers.forEach((h) => h(data));
        return;
      case "prefill-number":
        prefillHandlers.forEach((h) => h(data));
        return;
      case "command":
        commandHandlers.forEach((h) => h(data));
        return;
      case "incoming-call":
        incomingCallHandlers.forEach((h) => h(data));
        return;
      case "incoming-lead":
        incomingLeadHandlers.forEach((h) => h(data));
        return;
      case "transfer-outcome":
        transferOutcomeHandlers.forEach((h) => h(data));
        break;
      case "answer-failed":
        answerFailedHandlers.forEach((h) => h(data));
        return;
    }
  });

  function postFocusRequest(): void {
    const msg: SipFocusRequestMessage = { type: "focus-request", timestamp: Date.now() };
    channel.postMessage(msg);
  }

  function onFocusRequest(handler: () => void): void {
    focusHandlers.push(handler);
  }

  function postHeartbeat(isPopup: boolean, windowId: string): void {
    const msg: SipHeartbeatMessage = {
      type: "heartbeat",
      timestamp: Date.now(),
      isPopup,
      windowId,
    };
    channel.postMessage(msg);
  }

  function onHeartbeat(handler: (msg: SipHeartbeatMessage) => void): void {
    heartbeatHandlers.push(handler);
  }

  function postCallStateSnapshot(payload: SipCallStateSnapshot): void {
    const msg: SipCallStateSnapshotMessage = {
      type: "call-state-snapshot",
      timestamp: Date.now(),
      payload,
    };
    channel.postMessage(msg);
  }

  function onCallStateSnapshot(handler: (msg: SipCallStateSnapshotMessage) => void): void {
    snapshotHandlers.push(handler);
  }

  function requestSnapshot(): void {
    const msg: SipSnapshotRequestMessage = {
      type: "request-snapshot",
      timestamp: Date.now(),
    };
    channel.postMessage(msg);
  }

  function onSnapshotRequest(handler: () => void): void {
    snapshotRequestHandlers.push(handler);
  }

  function postPopupClosing(): void {
    const msg: SipPopupClosingMessage = {
      type: "popup-closing",
      timestamp: Date.now(),
    };
    channel.postMessage(msg);
  }

  function onPopupClosing(handler: () => void): void {
    popupClosingHandlers.push(handler);
  }

  function postPopupShutdown(reason: SipPopupShutdownMessage["reason"]): void {
    const msg: SipPopupShutdownMessage = {
      type: "popup-shutdown",
      timestamp: Date.now(),
      reason,
    };
    channel.postMessage(msg);
  }

  function onPopupShutdown(handler: (msg: SipPopupShutdownMessage) => void): void {
    popupShutdownHandlers.push(handler);
  }

  function postMainAlive(windowId: string): void {
    const msg: SipMainAliveMessage = {
      type: "main-alive",
      timestamp: Date.now(),
      windowId,
    };
    channel.postMessage(msg);
  }

  function onMainAlive(handler: (msg: SipMainAliveMessage) => void): void {
    mainAliveHandlers.push(handler);
  }

  function onPrefillNumber(handler: (msg: SipPrefillNumberMessage) => void): void {
    prefillHandlers.push(handler);
  }

  function postCommand(action: SipCommandAction, payload?: string | null): void {
    const msg: SipCommandMessage = { type: "command", timestamp: Date.now(), action, payload };
    channel.postMessage(msg);
  }

  function onCommand(handler: (msg: SipCommandMessage) => void): void {
    commandHandlers.push(handler);
  }

  function postIncomingCall(phoneNumber: string, callerLine?: string | null): void {
    const msg: SipIncomingCallMessage = {
      type: "incoming-call",
      timestamp: Date.now(),
      phoneNumber,
      callerLine: callerLine ?? null,
    };
    channel.postMessage(msg);
  }

  function onIncomingCall(handler: (msg: SipIncomingCallMessage) => void): void {
    incomingCallHandlers.push(handler);
  }

  function onIncomingLead(handler: (msg: SipIncomingLeadMessage) => void): void {
    incomingLeadHandlers.push(handler);
  }

  function postAnswerFailed(phoneNumber: string, reason: SipAnswerFailedMessage["reason"]): void {
    const msg: SipAnswerFailedMessage = {
      type: "answer-failed",
      timestamp: Date.now(),
      phoneNumber,
      reason,
    };
    channel.postMessage(msg);
  }

  function onAnswerFailed(handler: (msg: SipAnswerFailedMessage) => void): void {
    answerFailedHandlers.push(handler);
  }

  function postTransferOutcome(
    toExtension: string,
    outcome: SipTransferOutcomeMessage["outcome"]
  ): void {
    const msg: SipTransferOutcomeMessage = {
      type: "transfer-outcome",
      timestamp: Date.now(),
      toExtension,
      outcome,
    };
    channel.postMessage(msg);
  }

  function onTransferOutcome(handler: (msg: SipTransferOutcomeMessage) => void): void {
    transferOutcomeHandlers.push(handler);
  }

  onBeforeUnmount(() => {
    answerFailedHandlers.length = 0;
    transferOutcomeHandlers.length = 0;
    focusHandlers.length = 0;
    heartbeatHandlers.length = 0;
    snapshotHandlers.length = 0;
    snapshotRequestHandlers.length = 0;
    popupClosingHandlers.length = 0;
    popupShutdownHandlers.length = 0;
    mainAliveHandlers.length = 0;
    prefillHandlers.length = 0;
    commandHandlers.length = 0;
    incomingCallHandlers.length = 0;
    incomingLeadHandlers.length = 0;
    channel.close();
  });

  return {
    postFocusRequest,
    onFocusRequest,
    postHeartbeat,
    onHeartbeat,
    postCallStateSnapshot,
    onCallStateSnapshot,
    requestSnapshot,
    onSnapshotRequest,
    postPopupClosing,
    onPopupClosing,
    postPopupShutdown,
    onPopupShutdown,
    postMainAlive,
    onMainAlive,
    onPrefillNumber,
    postCommand,
    onCommand,
    postIncomingCall,
    onIncomingCall,
    onIncomingLead,
    postAnswerFailed,
    onAnswerFailed,
    postTransferOutcome,
    onTransferOutcome,
  };
}

// Canale dedicato (module-level) per i POST originati FUORI da un componente
// (es. azioni Pinia): non può usare useSipBroadcast(), che registra onBeforeUnmount.
let postOnlyChannel: BroadcastChannel | null = null;

/**
 * Pubblica un messaggio "prefill-number" sul canale gaia-sip. Pensato per contesti
 * non-componente (es. lo store SIP quando si clicca un numero): raggiunge la finestra
 * popup della barra (store Pinia separato) per pre-compilare l'input. Ricevuto via
 * useSipBroadcast().onPrefillNumber nel componente della barra.
 */
export function postPrefillNumber(phoneNumber: string): void {
  if (!postOnlyChannel) postOnlyChannel = new BroadcastChannel(CHANNEL_NAME);
  const msg: SipPrefillNumberMessage = {
    type: "prefill-number",
    timestamp: Date.now(),
    phoneNumber,
  };
  postOnlyChannel.postMessage(msg);
}

/**
 * Pubblica un messaggio "incoming-call" sul canale gaia-sip. Pensato per contesti
 * non-componente (es. lo store SIP o il servizio notifiche) che devono notificare
 * le view di una chiamata in arrivo senza poter usare useSipBroadcast().
 */
export function postIncomingCall(phoneNumber: string, callerLine?: string | null): void {
  if (!postOnlyChannel) postOnlyChannel = new BroadcastChannel(CHANNEL_NAME);
  const msg: SipIncomingCallMessage = {
    type: "incoming-call",
    timestamp: Date.now(),
    phoneNumber,
    callerLine: callerLine ?? null,
  };
  postOnlyChannel.postMessage(msg);
}

/**
 * Pubblica un messaggio "incoming-lead": la chiamata in arrivo appartiene a un
 * contatto già presente in Gaia, e questa è la sua scheda. Emesso dalla finestra
 * owner (onInvite) dopo che la lookup è tornata; consumato dalle view via
 * useSipBroadcast().onIncomingLead per offrire "Apri scheda" nella tab in cui
 * l'operatore sta effettivamente lavorando (GAIA-2719).
 */
export function postIncomingLead(phoneNumber: string, targetUrl: string): void {
  if (!postOnlyChannel) postOnlyChannel = new BroadcastChannel(CHANNEL_NAME);
  const msg: SipIncomingLeadMessage = {
    type: "incoming-lead",
    timestamp: Date.now(),
    phoneNumber,
    targetUrl,
  };
  postOnlyChannel.postMessage(msg);
}
